'use client';

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';

import { Maximize2, Minimize2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { PRIMARY_DEVICE } from '@/components/CanvasPage/canvasConstants';
import { CanvasPage } from '@/components/CanvasPage/CanvasPage';
import {
  BiLoggerContextProvider,
  useBiLoggerContext,
} from '@/components/Contexts/BiLoggerContext';
import {
  CommentsProvider,
  useCommentsContext,
} from '@/components/Contexts/CommentsContext';
import {
  PreviewFrameReadyProvider,
  usePreviewFrameReady,
} from '@/components/Contexts/PreviewFrameReadyContext';
import { SiteUrlProvider } from '@/components/Contexts/SiteUrlContext';
import {
  ViewerRoleProvider,
  useViewerRole,
} from '@/components/Contexts/ViewerRoleContext';
import { WixInstanceProvider } from '@/components/Contexts/WixInstanceContext';
import {
  WorkbenchConfigProvider,
  useWorkbenchConfig,
} from '@/components/Contexts/WorkbenchConfigContext';
import { GuestUserModal } from '@/components/GuestUserModal';
import { ProxyFrame } from '@/components/ProxyFrame/ProxyFrame';
import { countOpenPins } from '@/components/Sidebar/sidebarPins';
import { Toolbar } from '@/components/Toolbar/Toolbar';
import { ToolbarClient } from '@/components/Toolbar/ToolbarClient';
import { ToolbarZoomControl } from '@/components/Toolbar/ToolbarZoomControl';
import {
  CopiedToastProvider,
  ErrorToastProvider,
  NavigationToastProvider,
  showErrorToast,
} from '@/components/ui/ToastNotification';
import { ViewportContainer } from '@/components/ViewportContainer/ViewportContainer';
import { BI_CLICK } from '@/constants/bi';
import { useDeleteReply } from '@/hooks/useDeleteReply';
import { useMarkCommentRead } from '@/hooks/useMarkCommentRead';
import { useReopenComment } from '@/hooks/useReopenComment';
import { useReplyPersistence } from '@/hooks/useReplyPersistence';
import { useSubmitCommenter } from '@/hooks/useSubmitCommenter';
import {
  DEVICE_WIDTHS,
  EditorType,
  type DevicePreset,
  type InitializeData,
  type Pin,
} from '@/types/feedback';

import { FullscreenExitOverlay } from './FullscreenExitOverlay';
import { useDelayedFrameSpinner } from './useDelayedFrameSpinner';
import { useWorkbenchPageState } from './useWorkbenchPageState';
import { useWorkbenchPinPersistence } from './useWorkbenchPinPersistence';
import { useWorkbenchZoom } from './useWorkbenchZoom';
import styles from './WorkbenchPage.module.css';
import { WorkbenchPinPopoverLayer } from './WorkbenchPinPopoverLayer';
import { WorkbenchSidebarPanel } from './WorkbenchSidebarPanel';

interface WorkbenchPageClientProps {
  commentId?: string;
  sealedContext?: string;
  targetUrl: string;
  metaSiteId: string;
  feedbackId: string;
  editorType?: EditorType;
  initialData?: InitializeData;
  loader: ReactNode;
  allowAnonymousModal?: boolean;
  sidebarReadOnly?: boolean;
  isPartner?: boolean;
  agencyName?: string;
}

export function WorkbenchPageClient(props: WorkbenchPageClientProps) {
  return (
    <ViewerRoleProvider isPartner={props.isPartner ?? false}>
      <SiteUrlProvider siteUrl={props.targetUrl}>
        <PreviewFrameReadyProvider>
          <WixInstanceProvider siteUrl={props.targetUrl}>
            <CommentsProvider
              sealedContext={props.sealedContext}
              initialData={props.initialData}
            >
              <BiLoggerContextProvider
                metaSiteId={props.metaSiteId}
                editorType={props.editorType}
              >
                <WorkbenchConfigProvider
                  editorType={props.editorType}
                  metaSiteId={props.metaSiteId}
                  feedbackId={props.feedbackId}
                  sealedContext={props.sealedContext}
                  sidebarReadOnly={props.sidebarReadOnly ?? false}
                  // The reviewer page passes the agency name from its link
                  // info; the owner page gets it with its comments.
                  agencyName={props.agencyName ?? props.initialData?.agencyName}
                >
                  <WorkbenchPageContent {...props} />
                </WorkbenchConfigProvider>
              </BiLoggerContextProvider>
            </CommentsProvider>
          </WixInstanceProvider>
        </PreviewFrameReadyProvider>
      </SiteUrlProvider>
    </ViewerRoleProvider>
  );
}

function WorkbenchPageContent({
  commentId,
  targetUrl,
  loader,
  allowAnonymousModal = false,
}: WorkbenchPageClientProps) {
  const t = useTranslations();
  const { isPartner } = useViewerRole();
  const { editorType, metaSiteId, feedbackId, sidebarReadOnly } =
    useWorkbenchConfig();
  const { reportPageView, reportClick, setScreen } = useBiLoggerContext();
  const {
    state: {
      isLoaded,
      pins: contextPins,
      userInfo,
      commenterInfoMap,
      visitorId,
    },
    dispatch,
  } = useCommentsContext();
  const [pendingPin, setPendingPin] = useState<{
    partial: Omit<Pin, 'label' | 'createdAt'>;
    viewportX: number;
    viewportY: number;
  } | null>(null);
  const [guestModalDismissed, setGuestModalDismissed] = useState(false);
  const { mutate: submitCommenter, isPending: isSubmittingCommenter } =
    useSubmitCommenter();

  const isAnonymous = allowAnonymousModal && userInfo?.userName === 'Anonymous';

  function handleModalDismiss() {
    setGuestModalDismissed(true);
    setPendingPin(null);
  }

  function handleModalSubmit(name: string, email?: string) {
    reportClick({
      clickType: BI_CLICK.GUEST_USER_SUBMIT,
      commentHasEmail: Boolean(email),
    });
    submitCommenter(
      { name, email },
      {
        onSuccess: (result) => {
          dispatch({
            type: 'UPDATE_USER_INFO',
            payload: { userName: result.userName, email: result.email },
          });
          if (pendingPin) {
            handlePinAdded(
              pendingPin.partial,
              pendingPin.viewportX,
              pendingPin.viewportY,
            );
            setPendingPin(null);
          }
        },
        onError: () => showErrorToast(t('get-feedback-toast-submitFailed')),
      },
    );
  }
  const [isReady, setIsReady] = useState(false);
  // Latched on the first ready report and shared through context: the instance
  // fetch (and with it session + comments) waits for the frame, and a later
  // frame reload (device switch, navigation) clears isReady without re-blocking
  // those requests or flickering the guest modal away.
  const { frameEverReady, markFrameReady } = usePreviewFrameReady();

  const {
    device,
    setDevice,
    mode,
    setMode,
    view,
    setView,
    pins,
    visiblePins,
    pageFilteredPins,
    canvasTargetUrl,
    workbenchFrameUrl,
    currentPagePath,
    sidebarOpen,
    setSidebarOpen,
    fullscreen,
    setFullscreen,
    setActivePin,
    proxyFrameRef,
    canvasFrameRefs,
    handleFrameLoading: handlePendingScrollFrameLoading,
    handleFrameReady: handlePendingScrollFrameReady,
    pinMode,
    pinsVisible,
    handlePinAdded,
    handlePinMarkerClick,
    handleScrolledToPin,
    handleUpdatePinLabel,
    handlePageChanged,
    handleSidebarPinClick,
    pinPopoverModel,
  } = useWorkbenchPageState(targetUrl, isPartner, editorType);
  const { markCommentRead } = useMarkCommentRead(
    feedbackId,
    metaSiteId,
    isPartner,
  );

  const handlePinMarkerClickAndMarkRead = useCallback(
    (pinId: string, x: number, y: number) => {
      markCommentRead(pinId);
      handlePinMarkerClick(pinId, x, y);
    },
    [handlePinMarkerClick, markCommentRead],
  );

  const handleScrolledToPinAndMarkRead = useCallback(
    (pinId: string, x: number, y: number) => {
      markCommentRead(pinId);
      handleScrolledToPin(pinId, x, y);
    },
    [handleScrolledToPin, markCommentRead],
  );

  const handleSidebarPinClickAndMarkRead = useCallback(
    (pinId: string) => {
      markCommentRead(pinId);
      handleSidebarPinClick(pinId);
    },
    [handleSidebarPinClick, markCommentRead],
  );

  const handleFrameLoading = useCallback(() => {
    handlePendingScrollFrameLoading();
    setIsReady(false);
  }, [handlePendingScrollFrameLoading, setIsReady]);
  const handleFrameReady = useCallback(() => {
    handlePendingScrollFrameReady();
    setIsReady(true);
    markFrameReady();
  }, [handlePendingScrollFrameReady, setIsReady, markFrameReady]);

  // Every canvas frame reports its own readiness so a deferred scroll waits for
  // the frame it targets, while the page loader still follows the primary one.
  const handleCanvasFrameLoading = useCallback(
    (frameDevice: DevicePreset) => {
      handlePendingScrollFrameLoading(frameDevice);
      if (frameDevice === PRIMARY_DEVICE) setIsReady(false);
    },
    [handlePendingScrollFrameLoading, setIsReady],
  );
  const handleCanvasFrameReady = useCallback(
    (frameDevice: DevicePreset) => {
      handlePendingScrollFrameReady(frameDevice);
      if (frameDevice === PRIMARY_DEVICE) {
        setIsReady(true);
        markFrameReady();
      }
    },
    [handlePendingScrollFrameReady, setIsReady, markFrameReady],
  );

  function interceptPinAdded(
    partial: Omit<Pin, 'label' | 'createdAt'>,
    viewportX: number,
    viewportY: number,
  ) {
    // Starting a new pin implicitly closes whatever popover was open; if
    // that was an unsaved draft pin, clean it up instead of leaving an
    // orphan behind.
    if (pinPopoverOpen) handleDismissPinPopover();

    if (isAnonymous) {
      setGuestModalDismissed(false);
      setPendingPin({ partial, viewportX, viewportY });
      return;
    }
    handlePinAdded(partial, viewportX, viewportY);
  }

  const modeInitializedRef = useRef(false);
  useEffect(() => {
    if (isLoaded && contextPins.length > 0 && !modeInitializedRef.current) {
      modeInitializedRef.current = true;
      setMode('comment');
    }
  }, [isLoaded, contextPins.length, setMode]);

  const deepLinkedCommentHandledRef = useRef(false);
  useEffect(() => {
    if (
      !commentId ||
      !isLoaded ||
      !isReady ||
      currentPagePath === null ||
      deepLinkedCommentHandledRef.current
    ) {
      return;
    }

    const pin = pins.find((candidate) => candidate.commentId === commentId);
    if (!pin) {
      deepLinkedCommentHandledRef.current = true;
      return;
    }

    if (pin.devicePreset && pin.devicePreset !== device) {
      setDevice(pin.devicePreset);
      return;
    }

    deepLinkedCommentHandledRef.current = true;
    handleSidebarPinClickAndMarkRead(pin.id);
  }, [
    commentId,
    currentPagePath,
    device,
    handleSidebarPinClickAndMarkRead,
    isLoaded,
    isReady,
    pins,
    setDevice,
  ]);

  // Keep the ambient BI screen state (breakpoint + inspected page) current so
  // click events are enriched with the device and page the user is viewing.
  useEffect(() => {
    setScreen({ breakpoint: device, pageId: currentPagePath ?? undefined });
  }, [device, currentPagePath, setScreen]);

  // Report a page-loaded event when the workbench is ready, the bridge has
  // reported the inspected page path, and the visitor id is known, then again
  // on page navigation. On a client route the id arrives only after session
  // and comments resolve; reporting earlier would send the one-shot event
  // without it. breakpoint is read from a ref so a device switch alone does
  // not re-fire.
  const deviceRef = useRef(device);
  useEffect(() => {
    deviceRef.current = device;
  }, [device]);

  useEffect(() => {
    if (!isReady || currentPagePath === null || !visitorId) return;
    reportPageView({
      pageId: currentPagePath || undefined,
      breakpoint: deviceRef.current,
    });
  }, [isReady, currentPagePath, visitorId, reportPageView]);

  const {
    handleDismissPinPopover,
    handleSavePinPopover,
    handlePinMovedAndPersist,
    handleDeletePinAndPersist,
    handleResolvePinAndPersist,
    handleReopenPinAndPersist,
  } = useWorkbenchPinPersistence({
    targetUrl,
    feedbackId,
    metaSiteId,
    device,
    pinPopoverModel,
    setActivePin,
  });

  const { reopenComment } = useReopenComment();
  const { addReply } = useReplyPersistence();
  const { deleteReply } = useDeleteReply();

  const {
    zoomMultiplier,
    setEffectiveScale,
    zoomPercent,
    canZoomOut,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
  } = useWorkbenchZoom();

  useEffect(() => {
    if (!pinsVisible) setActivePin(null);
  }, [pinsVisible, setActivePin]);

  const showFrameSpinner = useDelayedFrameSpinner(isReady, device);
  const showPageLoader = !isLoaded;
  // Show on first load for anonymous users, or when they try to pin after
  // dismissing -- but only once the workbench is actually on screen: the page
  // loader gone and the preview frame having reported ready.
  const showAnonymousModal =
    isAnonymous &&
    frameEverReady &&
    !showPageLoader &&
    (!guestModalDismissed || !!pendingPin);
  const pinPopoverOpen = !!pinPopoverModel;
  // The sidebar selection mirrors the open comment, so dismissing the popover
  // drops the highlight instead of leaving it stuck on the last comment.
  const selectedPinId = pinPopoverModel?.pin.id ?? null;

  return (
    <NavigationToastProvider>
      <ErrorToastProvider>
        <CopiedToastProvider>
          <div className="relative flex flex-1 flex-col">
            {showPageLoader && loader}
            <Toolbar targetUrl={targetUrl} hidden={fullscreen}>
              <ToolbarClient
                mode={mode}
                onModeChange={setMode}
                view={view}
                onViewChange={setView}
                device={device}
                onDeviceChange={setDevice}
                sidebarOpen={sidebarOpen}
                onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
                hasUnread={
                  isPartner && pins.some((pin) => pin.status === 'NEW')
                }
                openCommentsCount={countOpenPins(pins)}
                isPartner={isPartner}
                metaSiteId={metaSiteId}
                targetUrl={targetUrl}
              />
            </Toolbar>
            <div
              className={`relative flex flex-1 overflow-hidden min-h-0 ${fullscreen ? '' : 'pt-12'} ${mode === 'browse' ? styles.browse : styles.comment}`}
            >
              {view === 'workbench' ? (
                <ViewportContainer
                  device={device}
                  fullscreen={fullscreen}
                  zoomMultiplier={zoomMultiplier}
                  onEffectiveScaleChange={setEffectiveScale}
                >
                  <ProxyFrame
                    ref={proxyFrameRef}
                    targetUrl={workbenchFrameUrl}
                    deviceWidth={DEVICE_WIDTHS[device]}
                    device={device}
                    fullscreen={fullscreen}
                    pins={visiblePins}
                    commenterInfoMap={commenterInfoMap}
                    pinMode={pinMode}
                    inspectOnly={isPartner}
                    pinsVisible={pinsVisible}
                    scrollLocked={pinPopoverOpen}
                    selectionDisabled={pinPopoverOpen}
                    onLoading={handleFrameLoading}
                    onReady={handleFrameReady}
                    showLoadingOverlay={isLoaded && showFrameSpinner}
                    contentVisible={isReady}
                    onPinAdded={interceptPinAdded}
                    onPinMarkerClick={handlePinMarkerClickAndMarkRead}
                    onPinMoved={handlePinMovedAndPersist}
                    onScrolledToPin={handleScrolledToPinAndMarkRead}
                    onPageChanged={handlePageChanged}
                    onDismissPopover={handleDismissPinPopover}
                  />
                </ViewportContainer>
              ) : (
                <CanvasPage
                  targetUrl={canvasTargetUrl}
                  frameRefs={canvasFrameRefs}
                  pins={pageFilteredPins}
                  commenterInfoMap={commenterInfoMap}
                  onPageChanged={handlePageChanged}
                  pinMode={pinMode}
                  inspectOnly={isPartner}
                  pinsVisible={pinsVisible}
                  scrollLocked={pinPopoverOpen}
                  selectionDisabled={pinPopoverOpen}
                  onFrameLoading={handleCanvasFrameLoading}
                  onFrameReady={handleCanvasFrameReady}
                  showFrameSpinner={isLoaded && showFrameSpinner}
                  frameContentVisible={isReady}
                  onPinAdded={interceptPinAdded}
                  onPinMarkerClick={handlePinMarkerClickAndMarkRead}
                  onPinMoved={handlePinMovedAndPersist}
                  onScrolledToPin={handleScrolledToPinAndMarkRead}
                  onDismissPopover={handleDismissPinPopover}
                />
              )}
              {view === 'workbench' && (
                <div className="absolute bottom-4 left-4 z-40 flex items-center gap-1.5">
                  {/* Zoom has no effect in fullscreen (the frame is fixed at
                      scale(1)), so only show it in the normal workbench view. */}
                  {!fullscreen && (
                    <ToolbarZoomControl
                      zoomPercent={zoomPercent}
                      canZoomOut={canZoomOut}
                      onZoomIn={() => {
                        reportClick({ clickType: BI_CLICK.ZOOM_IN });
                        handleZoomIn();
                      }}
                      onZoomOut={() => {
                        reportClick({ clickType: BI_CLICK.ZOOM_OUT });
                        handleZoomOut();
                      }}
                      onResetZoom={() => {
                        reportClick({ clickType: BI_CLICK.ZOOM_RESET });
                        handleResetZoom();
                      }}
                    />
                  )}
                  {/* Fullscreen stretches the frame to full width, which would
                      distort the mobile layout, so it's not offered on mobile. */}
                  {device !== 'mobile' && (
                    <button
                      type="button"
                      onClick={() => {
                        reportClick({ clickType: BI_CLICK.FULLSCREEN });
                        setFullscreen((f) => !f);
                      }}
                      title={t(
                        fullscreen
                          ? 'get-feedback-workbench-exitFullscreen'
                          : 'get-feedback-toolbar-enterFullscreen',
                      )}
                      aria-label={t(
                        fullscreen
                          ? 'get-feedback-workbench-exitFullscreen'
                          : 'get-feedback-toolbar-enterFullscreen',
                      )}
                      className="flex items-center justify-center rounded-full border border-slate-200 bg-background p-2 shadow-lg transition-all hover:bg-blue-50 hover:text-blue-950"
                    >
                      {fullscreen ? (
                        <Minimize2 className="h-3.5 w-3.5 text-blue-950" />
                      ) : (
                        <Maximize2 className="h-3.5 w-3.5 text-blue-950" />
                      )}
                    </button>
                  )}
                </div>
              )}
              <WorkbenchSidebarPanel
                visible={sidebarOpen && !fullscreen}
                pins={pins}
                device={device}
                allDevices={view === 'canvas'}
                mode={sidebarReadOnly ? 'browse' : mode}
                commenterInfoMap={commenterInfoMap}
                selectedPinId={selectedPinId}
                onPinClick={handleSidebarPinClickAndMarkRead}
                onReopenPin={
                  isPartner ? handleReopenPinAndPersist : reopenComment
                }
                onClose={() => setSidebarOpen(false)}
              />
            </div>
            {fullscreen && (
              <FullscreenExitOverlay onExit={() => setFullscreen(false)} />
            )}
            <WorkbenchPinPopoverLayer
              model={pinPopoverModel}
              commenterInfoMap={commenterInfoMap}
              sidebarVisible={sidebarOpen && !fullscreen}
              onDismiss={handleDismissPinPopover}
              onSave={handleSavePinPopover}
              onUpdatePinLabel={handleUpdatePinLabel}
              onDeletePin={handleDeletePinAndPersist}
              onResolvePin={handleResolvePinAndPersist}
              onReopenPin={
                isPartner ? handleReopenPinAndPersist : reopenComment
              }
              onAddReply={(pinId, message) => {
                const pin = pins.find((p) => p.id === pinId);
                if (pin) addReply(pin, message);
              }}
              onDeleteReply={(pinId, replyIndex) => {
                const pin = pins.find((p) => p.id === pinId);
                if (pin) deleteReply(pin, replyIndex);
              }}
            />
            {showAnonymousModal && (
              <GuestUserModal
                onSubmit={handleModalSubmit}
                onDismiss={handleModalDismiss}
                isSubmitting={isSubmittingCommenter}
              />
            )}
          </div>
        </CopiedToastProvider>
      </ErrorToastProvider>
    </NavigationToastProvider>
  );
}
